import React, { useState, useEffect, useMemo, useCallback } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import axios from "axios";
import "../Style/Dashboard.css";
import "../Style/FinancialTables.css";
import { useFilterContext } from "./FilterContext";
import { BASE_URL } from "../Apis/apiConfig";
import {Eye, Edit, Trash2, Search, RefreshCw, FileX, AlertCircle, CheckSquare, X, Download, Building2, Users} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const PreviousYearAdvancePayment = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [wings, setWings] = useState([]);
  const [selectedWing, setSelectedWing] = useState("");
  const { filteredData: filteredContextData } = useFilterContext() || {};
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const societyName = localStorage.getItem("society") ?? "";
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(
        `${BASE_URL}/advance-payment/get-previous-advance-payment`,
        {
          headers,
          params: { societyName },
          validateStatus: null,
        },
      );

      if (response.status === 200) {
        const payload = response.data;
        if (payload == null) setData([]);
        else if (Array.isArray(payload)) setData(payload);
        else if (Array.isArray(payload.data)) setData(payload.data);
        else setData([]);
      } else {
        setError(
          response.data?.message ||
            `Failed to fetch advance payments (status ${response.status})`,
        );
      }
    } catch (err) {
      console.error("fetchData error:", err);
      setError("Failed to fetch advance payments");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const uniqueWings = Array.from(new Set(data.map((d) => d.wing))).sort();
    setWings(uniqueWings);
  }, [data]);

  const getField = (obj, keys = []) => {
    if (!obj) return "";
    for (const k of keys) {
      const v = obj[k];
      if (v !== undefined && v !== null && String(v).trim() !== "")
        return String(v);
    }
    return "";
  };

  const filteredTableData = useMemo(() => {
    let src =
      filteredContextData && filteredContextData.length > 0
        ? filteredContextData
        : data;

    if (selectedWing) {
      src = src.filter((d) => d.wing === selectedWing);
    }

    if (!searchTerm.trim()) return src;

    const searchLower = searchTerm.toLowerCase();
    return src.filter((d) => {
      const name = getField(d, ["member_name", "MemberName", "name"]) || "";
      const unitNo = getField(d, ["unit_no", "Unit_No", "Flat", "flat"]) || "";
      const amount = getField(d, ["Amount", "amount"]) || "";

      return (
        name.toLowerCase().includes(searchLower) ||
        unitNo.toLowerCase().includes(searchLower) ||
        amount.toLowerCase().includes(searchLower)
      );
    });
  }, [data, filteredContextData, selectedWing, searchTerm]);
  const totalItems = filteredTableData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [perPage, totalItems, currentPage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredTableData.slice(start, start + perPage);
  }, [filteredTableData, currentPage, perPage]);

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${
              totalPages === currentPage ? "active" : ""
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }

    return pages;
  };

  const deleteByAdminBillTable = async () => {
    const token = localStorage.getItem("token");
    const societyName = localStorage.getItem("society");
    if (!societyName) {
      Swal.fire("Error", "Society name is required", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: `This will permanently delete ALL advance payments for "${societyName}"!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.delete(
        `${BASE_URL}/advance-payment/delete-table-by-admin`,
        {
          params: { societyName },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.fire(
        "Deleted!",
        response.data || "Advance payments deleted successfully!",
        "success",
      );
      await fetchData();
      setSelectedRows([]);
    } catch (error) {
      console.error(error);
      Swal.fire(
        "Error",
        error.response?.data || "Failed to delete advance payments",
        "error",
      );
    }
  };

  const deleteSelectedMembers = async () => {
    if (selectedRows.length === 0) {
      Swal.fire("Info", "Please select advance payments to delete", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `This will delete ${selectedRows.length} selected advance payment(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society") || "";
      const response = await axios.delete(
        `${BASE_URL}/advance-payment/delete-table-by-admin-multiple`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            societyName,
            multipleIds: selectedRows.map(Number),
          },
        },
      );

      Swal.fire(
        "Deleted!",
        response.data || "Selected advance payments deleted successfully",
        "success",
      );
      await fetchData();
      setSelectedRows([]);
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.response?.data || "Failed to delete selected advance payments",
        "error",
      );
    }
  };

  const selectAllRows = (e) => {
    if (e.target.checked) {
      const allIds = paginatedData
        .map((item) => item.id ?? item._id)
        .filter((id) => id !== undefined);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    if (!id) return;

    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleViewItem = (item) => {
    const name = getField(item, ["member_name", "MemberName", "name"]) || "-";
    const unitNo =
      getField(item, ["unit_no", "Unit_No", "Flat", "flat"]) || "-";
    const amount = getField(item, ["Amount", "amount"]) || "-";
    const wing = getField(item, ["wing", "Wing"]) || "-";

    Swal.fire({
      title: "Advance Payment Details",
      html: `
        <div style="text-align: left; font-family: monospace;">
          <div style="margin-bottom: 8px;">
            <strong>Member Name:</strong> ${name}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Unit No:</strong> ${unitNo}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Wing:</strong> ${wing}
          </div>
          <div style="margin-bottom: 8px;">
            <strong>Amount:</strong> ₹${amount}
          </div>
        </div>
      `,
      width: "500px",
      confirmButtonText: "Close",
    });
  };

  const handleAddPayment = () => {
    navigate("/add-advance-payment");
  };

  // Format amount with currency symbol
  const formatAmount = (amount) => {
    if (amount === "-" || !amount) return "-";
    const num = parseFloat(amount);
    return isNaN(num) ? amount : `₹${num.toLocaleString("en-IN")}`;
  };

  return (
    <div className="app-container">
      <style>
        {`
/* ========== ADVANCE PAYMENT PAGE OPTIMIZATIONS ========== */
.app-container {
  height: 100vh;
  overflow: hidden !important;
}

.main-layout {
  height: calc(100vh - 64px);
  overflow: hidden !important;
}

.content-area {
  height: 100%;
  overflow-y: auto !important;
  overflow-x: hidden;
  padding-bottom: 20px;
}

.financial-container,
.financial-card {
  height: auto !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 !important;
}

.financial-body {
  height: auto !important;
  overflow: visible !important;
  padding: 0 !important;
}

/* ========== COMPACT TABLE CONTROLS ========== */
.controls-left,
.controls-center,
.controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.controls-center {
  flex: 1;
  justify-content: center;
}

.entries-selector label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.entries-select {
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: white;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
  height: 28px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
}

.pagination-nav {
  padding: 4px 10px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  height: 26px;
  transition: all 0.2s;
}

.pagination-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-nav:not(:disabled):hover {
  background: #f1f5f9;
}

.pagination-btn {
  min-width: 26px;
  height: 26px;
  padding: 4px 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover {
  background: #f1f5f9;
}

.pagination-btn.active {
  background: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.pagination-ellipsis {
  padding: 0 4px;
  color: #94a3b8;
  font-size: 11px;
}

.entries-info {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

/* ========== TABLE OPTIMIZATION ========== */
.financial-table-container {
  overflow-x: auto;
  padding: 0;
  margin: 0;
}

.financial-table,
.data-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.financial-table th,
.data-table th {
  padding: 6px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  white-space: nowrap;
  font-weight: 600;
  font-size: 11px !important;
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.2 !important;
  height: 32px !important;
}

.financial-table td,
.data-table td {
  padding: 5px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  font-size: 10px !important;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.3 !important;
  height: 36px !important;
}

/* Checkbox Column */
.financial-table th:nth-child(1),
.financial-table td:nth-child(1),
.data-table th:first-child,
.data-table td:first-child {
  width: 35px !important;
  min-width: 35px !important;
  max-width: 35px !important;
  padding: 5px 2px !important;
}

/* Actions Column */
.financial-table th:last-child,
.financial-table td:last-child,
.data-table th:last-child,
.data-table td:last-child {
  width: 120px !important;
  min-width: 120px !important;
  padding: 4px 2px !important;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin: 0;
}

.action-icon-btn,
.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  width: 24px;
  height: 24px;
}

.action-icon-btn svg,
.icon-btn svg {
  width: 14px;
  height: 14px;
}

.action-icon-btn:hover,
.icon-btn:hover {
  background-color: #f1f5f9;
}

.icon-btn.edit {
  color: #0d6efd;
}

/* Checkbox */
.financial-table input[type="checkbox"],
.data-table input[type="checkbox"] {
  margin: 0;
  width: 13px;
  height: 13px;
  vertical-align: middle;
  cursor: pointer;
}

/* Row Hover */
.financial-table tbody tr:hover td,
.data-table tbody tr:hover td {
  background-color: #f8fafc;
}

.selected-row td {
  background-color: #e3f2fd !important;
}

/* Cell Content */
.cell-content {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Empty State */
.financial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 300px;
}

.financial-state h3 {
  margin: 16px 0 8px 0;
  color: #334155;
  font-size: 18px;
  font-weight: 600;
}

.financial-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Loading Animation */
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .controls-left,
  .controls-center,
  .controls-right {
    width: 100%;
    justify-content: space-between;
  }
}

/* Specific for advance payment table */
.compact-table thead th {
  white-space: normal !important;
  text-align: center;
  vertical-align: middle;
  line-height: 1.15;
  padding: 6px 4px !important;
  font-size: 10px !important;
}

.compact-table thead th {
  max-width: 100px;
}

.compact-table thead tr {
  height: 42px;
}

.reduceHeight {
  height: 36px !important;
  line-height: 1.2 !important;
}

.reduceHeight td {
  padding: 5px 6px !important;
  font-size: 10px !important;
  vertical-align: middle !important;
}

.reduceHeight .cell-content {
  max-height: 24px !important;
  overflow: hidden !important;
  text-align: center !important;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.reduceHeight .icon-btn {
  padding: 2px !important;
  width: 24px !important;
  height: 24px !important;
}

.reduceHeight .icon-btn svg {
  width: 12px !important;
  height: 12px !important;
}

.reduceHeight input[type="checkbox"] {
  width: 12px !important;
  height: 12px !important;
}

/* Wing info styling */
.wing-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.wing-info svg {
  flex-shrink: 0;
}

/* Amount badge */
.amount-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #d4edda;
  color: #155724;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

/* Member info styling */
.member-info strong {
  color: #087db7;
  font-size: 11px;
}

/* Wing dropdown */
.wing-dropdown {
  min-width: 120px;
}

.wing-dropdown .form-input {
  height: 28px;
  font-size: 12px;
  padding: 4px 8px;
}
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="financial-container">
            <div className="financial-card">
              {/* SINGLE COMPACT CONTROL ROW - Matching Receipt.js */}
              <div className="flex px-4 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-tr-lg rounded-tl-lg text-white py-2">
                {/* Left - Entries Selector */}
                <div className="controls-left">
                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>

                {/* Center - Search and Action Buttons */}
                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    {/* Search Input - Compact */}
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search advance payments..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>

                    {/* Wing Filter */}
                    <div className="wing-dropdown">
                      <select
                        value={selectedWing}
                        onChange={(e) => {
                          setSelectedWing(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="form-input"
                        style={{
                          height: "28px",
                          fontSize: "12px",
                          padding: "4px 8px",
                        }}
                      >
                        <option value="">All Wings</option>
                        {wings.map((wing) => (
                          <option key={wing} value={wing}>
                            {wing}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Action Buttons - Compact */}
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={fetchData}
                      disabled={loading}
                      title="Refresh advance payments list"
                    >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none flex align-center bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={deleteByAdminBillTable}
                      title="Delete all advance payments"
                    >
                      <Trash2 size={14} />
                    </button>

                    <button
                      className="rounded border-none gap-2 flex items-center text-left bg-red-300 text-white hover:bg-red-500 px-2 py-1"
                      onClick={deleteSelectedMembers}
                      disabled={selectedRows.length === 0}
                      title={`Delete ${selectedRows.length} selected advance payment(s)`}
                    >
                      <Trash2 size={14} />
                      <span style={{ fontSize: "11px" }}>
                        {selectedRows.length}
                      </span>
                      <CheckSquare size={14} />
                    </button>
                  </div>
                </div>

                {/* Right - Pagination */}
                <div className="controls-right">
                  {!loading && totalItems > 0 && (
                    <span className="entries-info text-white">
                      {(currentPage - 1) * perPage + 1}-
                      {Math.min(currentPage * perPage, totalItems)} of{" "}
                      {totalItems}
                    </span>
                  )}

                  <div className="flex gap-2 p-2">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>

                    {renderPageNumbers()}

                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Advance Payments</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={fetchData}
                      style={{
                        marginTop: "20px",
                        padding: "10px 20px",
                        background: "#0d6efd",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                ) : data.length === 0 ? (
                  <div className="financial-state">
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "40px",
                        borderRadius: "16px",
                        textAlign: "center",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <Users size={64} color="#94a3b8" />
                      <h3
                        style={{
                          margin: "20px 0 10px 0",
                          color: "#334155",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        No Advance Payments Found
                      </h3>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {searchTerm || selectedWing
                          ? "No advance payments match your search criteria."
                          : "There are no advance payments available for this society."}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          style={{
                            marginTop: "20px",
                            padding: "8px 20px",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="data-table compact-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              onChange={selectAllRows}
                              checked={
                                selectedRows.length > 0 &&
                                selectedRows.length ===
                                  paginatedData.filter(
                                    (item) => item.id || item._id,
                                  ).length
                              }
                            />
                          </th>
                          <th>Sr No</th>
                          <th>Member Name</th>
                          <th>Wing & Flat No</th>
                          <th>Amount</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((item, idx) => {
                          const itemId = item.id ?? item._id;
                          const srNo =
                            getField(item, ["Sr_No", "sr_no"]) ||
                            idx + 1 + (currentPage - 1) * perPage;
                          const name =
                            getField(item, [
                              "member_name",
                              "MemberName",
                              "name",
                            ]) || "-";
                          const unitNo =
                            getField(item, [
                              "unit_no",
                              "Unit_No",
                              "Flat",
                              "flat",
                            ]) || "-";
                          const amount =
                            getField(item, ["Amount", "amount"]) || "-";

                          return (
                            <tr
                              key={itemId ?? idx}
                              className={`reduceHeight ${
                                selectedRows.includes(itemId)
                                  ? "selected-row"
                                  : ""
                              }`}
                            >
                              <td>
                                {itemId && (
                                  <input
                                    type="checkbox"
                                    checked={selectedRows.includes(itemId)}
                                    onChange={() => handleRowSelect(itemId)}
                                  />
                                )}
                              </td>
                              <td>{srNo}</td>
                              <td>
                                <div className="member-info">
                                  <strong>{name}</strong>
                                </div>
                              </td>
                              <td>
                                <div className="wing-info">
                                  <Building2 size={12} />
                                  <span>{unitNo}</span>
                                </div>
                              </td>
                              <td>
                                <span className="amount-badge">
                                  {formatAmount(amount)}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="icon-btn edit"
                                    title="View Details"
                                    onClick={() => handleViewItem(item)}
                                  >
                                    <Eye size={14} />
                                  </button>
                                  <button
                                    className="icon-btn edit"
                                    title="Edit Payment"
                                    onClick={() => {
                                      Swal.fire(
                                        "Info",
                                        "Edit advance payment functionality coming soon",
                                        "info",
                                      );
                                    }}
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    className="icon-btn edit"
                                    title="Download Receipt"
                                    onClick={() => {
                                      Swal.fire(
                                        "Info",
                                        "Download receipt functionality coming soon",
                                        "info",
                                      );
                                    }}
                                  >
                                    <Download size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Floating Add Button */}
          {/* <button
            className="floating-add-btn"
            title="Add New Advance Payment"
            onClick={handleAddPayment}
            style={{
              position: "fixed",
              bottom: "30px",
              right: "30px",
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: "#0d6efd",
              color: "white",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13, 110, 253, 0.3)",
              transition: "all 0.2s",
              zIndex: 1000,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button> */}
        </main>
      </div>
    </div>
  );
};

export default PreviousYearAdvancePayment;
